<template>
  <v-data-table class="SignedDocumentsTable"
                data-locator="signed-documents-table"
                show-select
                :custom-key-sort="customKeySort"
                :items="signedDocuments"
                :items-per-page="itemsPerPage"
                :headers="headers"
                :page="currentPage"
  >
    <template #header.data-table-select>
      <v-checkbox-btn :model-value="areAllSelectedInCurrentPage"
                      :indeterminate="areSomeSelectedInCurrentPage"
                      data-locator="select-all-checkbox"
                      @update:model-value="$emit('toggle-all-selection')"
      />
    </template>

    <template #item.data-table-select="{ item }">
      <v-checkbox-btn v-if="item.proofFileURL || item.signedDocumentURL"
                      :model-value="isSignedDocumentSelected(item)"
                      data-locator="select-checkbox"
                      @update:model-value="toggleSignedDocumentSelection(item)"
      />
    </template>

    <template #item.name="{ value }">
      <span data-locator="file-name">
        {{ value }}
      </span>
    </template>

    <template #item.status="{ value }">
      <AppSignatureStatus :status="value" />
    </template>
    <template #item.date="{ value }">
      <span data-locator="signature-date">
        {{ ISOToShortDate(value) }}
      </span>
    </template>
    <template #item.signatories="{ item, value }">
      <span class="SignedDocumentsTable-signatoriesLink" data-locator="display-signatories-link" @click="openSignatoriesListDialog(item)">
        {{ value }}
      </span>
    </template>
    <template #item.options="{ item }">
      <SignedDocumentsTableItemOptions :item="item" />
    </template>
    <!-- an empty bottom remove the native pagination -->
    <template #bottom />
  </v-data-table>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'

import AppSignatureStatus from '@/common/AppSignatureStatus.vue'
import { ISOToShortDate } from '@/common/utils/dates'
import { dateDataMap, statusDataMap } from '@/project/signed-documents/signedDocumentsDataMap'

import SignedDocumentsTableItemOptions from './SignedDocumentsTableItemOptions.vue'

export default {
  name: 'SignedDocumentsTable',
  inheritAttrs: false,
  components: {
    AppSignatureStatus,
    SignedDocumentsTableItemOptions,
  },
  inject: [
    'isSignedDocumentSelected',
    'openSignatoriesListDialog',
    'toggleSignedDocumentSelection',
  ],
  props: {
    signedDocuments: {
      type: Array,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      required: true,
    },
    areAllSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
    areSomeSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      headers: [
        {
          title: this.$t('project.signed-documents.SignedDocumentsTable.headers.projectName'),
          class: ['text-caption', 'text-primary', 'text-uppercase', 'font-weight-semi-bold'],
          value: 'name',
          sortable: true,
        },
        {
          title: this.$t('project.signed-documents.SignedDocumentsTable.headers.status'),
          class: ['text-caption', 'text-primary', 'text-uppercase', 'font-weight-semi-bold'],
          key: 'status',
          value: signedDocument => statusDataMap(signedDocument),
        },
        {
          title: this.$t('project.signed-documents.SignedDocumentsTable.headers.date'),
          class: ['text-caption', 'text-primary', 'text-uppercase', 'font-weight-semi-bold'],
          key: 'date',
          value: signedDocument => dateDataMap(signedDocument),
          sortable: true,
        },
        {
          title: this.$t('project.signed-documents.SignedDocumentsTable.headers.signatories'),
          class: ['text-caption', 'text-primary', 'text-uppercase', 'font-weight-semi-bold'],
          key: 'signatories',
          value: signedDocument => {
            const { mySignature, otherSignatures } = signedDocument
            return mySignature ? 1 + otherSignatures.length : otherSignatures.length
          },
          sortable: true,
        },
        {
          value: 'options',
        },
      ],
      ISOToShortDate,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUserId']),
    customKeySort () {
      return {
        'name': (a, b) => a.localeCompare(b),
        'date': (a, b) => dayjs(a).isBefore(dayjs(b)) ? -1 : 1,
      }
    },
  },
}
</script>

<style scoped lang="scss">
.SignedDocumentsTable-signatoriesLink {
  cursor: pointer;
  &:hover {
    color: rgb(var(--v-theme-primary));
    text-decoration: underline;
  }
}

.SignedDocumentsTable {
  :deep(table) {
    tbody > tr > td:first-of-type > .v-selection-control--disabled {
      display: none;
    }

    tr {
      th:first-of-type,
      td:first-of-type {
        .v-selection-control__input > i.v-icon {
          font-size: 18px !important;

          .fa-square-check {
            color: rgb(var(--v-theme-primary));
          }
        }
      }
    }
  }
}
</style>
